"use client";

import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { careerApi } from "@/lib/api/services/career.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, ClipboardCheck, TrendingUp } from "lucide-react";
import Link from "next/link";
import { toast } from "@/components/ui/toast";

interface Assessment {
  id: string;
  title: string;
  description: string;
  duration: string;
  questions: number;
  category: string;
}

export default function CareerAssessmentsPage() {
  const queryClient = useQueryClient();

  const { data: assessments = [], isLoading, isError } = useQuery({
    queryKey: ["career-assessments"],
    queryFn: async () => {
      const data = await careerApi.getAssessments();
      return ((data as unknown as { assessments?: Assessment[] }).assessments ?? []) as Assessment[];
    },
  });

  const startMutation = useMutation({
    mutationFn: (assessmentId: string) => careerApi.startAssessment(assessmentId),
    onSuccess: () => {
      toast({ type: "success", title: "Assessment started", message: "Good luck with your assessment!" });
    },
    onError: () => {
      toast({ type: "error", title: "Error", message: "Failed to start assessment. Please try again." });
    },
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-2xl font-bold text-foreground">Assessments</h1>
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="h-5 w-2/3 bg-muted/10 rounded animate-pulse" />
                <div className="mt-2 h-3 w-full bg-muted/10 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-2xl font-bold text-foreground">Assessments</h1>
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="mx-auto h-12 w-12 text-destructive/50 animate-spin" />
            <h3 className="mt-4 text-lg font-medium text-foreground">Failed to load assessments</h3>
            <p className="mt-2 text-sm text-muted-foreground">Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/career">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Link>
        </Button>
      </div>

      <div className="space-y-1">
        <h1 className="text-xl font-display font-semibold tracking-tight text-foreground">Assessments</h1>
        <p className="text-sm text-muted-foreground">Evaluate your skills and discover growth areas.</p>
      </div>

      {assessments.length > 0 ? (
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {assessments.map((assessment) => (
            <Card key={assessment.id} className="hover:border-border/80 transition-colors duration-150">
              <CardContent className="p-4">
                <h3 className="text-sm font-medium text-foreground">{assessment.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{assessment.description}</p>
                <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><ClipboardCheck className="h-3.5 w-3.5" /> {assessment.questions} questions</span>
                  <span className="flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> {assessment.duration}</span>
                </div>
                <Badge variant="outline" className="mt-3 capitalize">{assessment.category}</Badge>
                <Button className="mt-3 w-full" size="sm" onClick={() => startMutation.mutate(assessment.id)} disabled={startMutation.isPending}>
                  Start Assessment
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <ClipboardCheck className="mx-auto h-10 w-10 text-muted-foreground/30" />
            <h3 className="mt-4 text-base font-medium text-foreground">No assessments available</h3>
            <p className="mt-1 text-sm text-muted-foreground">Check back later for new assessments.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}